import React, { useState, useEffect } from "react";
import { StyleSheet, View, Keyboard, Platform } from "react-native";

import moment from "moment";

import {
  hasItems,
  getTimeFormat,
  getStorageItem,
} from "../../../helpers/utils";

import Dialog from "../common/dialog";
import TimePickerSwipe from "../common/timepicker/TimePickerSwipe";
import ManaButton from "../common/ManaButton";
import JournalHelper from "./JournalHelper";
import AppMessages from "../../../config/AppMessages";
import TimePickerSwipeInfo from "../common/timepicker/TimePickerSwipeInfo";

const DATE_FORMAT = "YYYY-MM-DD HH:mm A";

export default function JournalTimePicker({
  controller,
  timeslot,
  minutesInterval = 15,
  allTimeslots = [],
  onClose = () => {},
  toggleTimePicker = () => {},
  updateMariMessage = () => {},
  use24hFormat = false,
  showTutorials = false,
  disableStartTime = false,
  disableEndTime = false,
}) {
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState(null);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const timeFormat = getTimeFormat(use24hFormat);

  useEffect(() => {
    getStorageItem("manual_time_picker").then((data) => {
      setIsManualEntry(!!data);
    });

    setSelectedTimeslot(
      timeslot
        ? {
            ...timeslot,
            //hasUpdatedEndTime: true,
            endTime: timeslot.endTime
              ? moment(timeslot.endTime, "h:mm A")
                  //.add(minutesInterval, "minutes") //no longer adding a time interval
                  .format("h:mm A")
              : "",
          }
        : null,
    );
  }, [timeslot]);

  const saveTimeslotIntervals = (model) => {
    Keyboard.dismiss();

    let { timeslotIntervals } = JournalHelper.getAffectedTimeslotIds(
      model,
      allTimeslots,
    );

    // Overlapping intervals
    if (hasItems(timeslotIntervals)) {
      let { 0: first, length: l, [l - 1]: last } = timeslotIntervals;

      updateMariMessage(
        l > 1
          ? `${AppMessages.TimeslotsOverrideWarning} from ${first.start} to ${last.end}!`
          : `${AppMessages.TimeslotsOverrideWarning} ${first.start}!`,
      );
      setRequiresConfirmation(true);
    } else {
      confirmTimeslotDuration(model);
    }
  };

  const confirmTimeslotDuration = (model) => {
    updateMariMessage("");
    setRequiresConfirmation(false);
    toggleTimePicker(model);
  };

  const onTimeChanged = (selectedDateTime, isStartTime) => {
    if (!selectedTimeslot) return;

    // If users changes times reset button and mari message
    setRequiresConfirmation(false);
    updateMariMessage("");

    let model = {
      ...selectedTimeslot,
      hasUpdatedEndTime: true,
    };

    // Start date
    if (isStartTime) {
      const { startDate, endTime } = selectedTimeslot;

      const newStartDateTime = moment(selectedDateTime, DATE_FORMAT);

      let endDateTime = moment(`${startDate} ${endTime}`, DATE_FORMAT);
      if (endDateTime.isBefore(newStartDateTime)) {
        endDateTime = endDateTime.add(1, "days");
      }

      model = {
        ...model,
        time: newStartDateTime.format("YYYY-MM-DD HH:mm:ss"),
        startDate: newStartDateTime.format("YYYY-MM-DD"),
        startTime: newStartDateTime.format(timeFormat),
        endDate: endDateTime.format("YYYY-MM-DD HH:mm:ss"),
        endTime: endDateTime.format(timeFormat),
      };

      // End date
    } else {
      const { startDate, startTime } = selectedTimeslot;
      const startDateTime = moment(`${startDate} ${startTime}`, DATE_FORMAT);

      let endDateTime = moment(selectedDateTime, DATE_FORMAT);
      if (endDateTime.isBefore(startDateTime)) {
        endDateTime = endDateTime.add(1, "days");
      }

      model = {
        ...model,
        endDate: endDateTime.format("YYYY-MM-DD HH:mm:ss"),
        endTime: endDateTime.format(timeFormat),
      };
    }

    setSelectedTimeslot(model);

    // Manual entry need button click to update
    if (!isManualEntry) {
      saveTimeslotIntervals(model);
    }
  };

  const toggleManualEntry = () => {
    if (disableEndTime || disableStartTime) return;
    setIsManualEntry((prev) => !prev);
  };

  return (
    <>
      {selectedTimeslot ? (
        <Dialog.Container
          onClose={onClose}
          showCloseButton
          headerStyle={{ marginHorizontal: 0, marginVertical: 0 }}
          contentStyle={{
            marginTop: 0,
            paddingTop: 0,
            paddingHorizontal: 0,
            paddingBottom: 10,
          }}
        >
          <TimePickerSwipe
            use24hFormat={use24hFormat}
            timeslot={selectedTimeslot}
            minutesInterval={minutesInterval}
            onTimeChanged={onTimeChanged}
            isManualEntry={isManualEntry}
            showTutorials={showTutorials}
            disableStartTime={disableStartTime}
            disableEndTime={disableEndTime}
          />

          {/* Buttons */}
          <View style={styles.buttonsContainer}>
            <ManaButton
              text={isManualEntry ? "Quick Entry" : "Manual Entry"}
              onPress={toggleManualEntry}
              style={[
                styles.button,
                disableEndTime || disableStartTime ? { opacity: 0.2 } : {},
                !isManualEntry && !requiresConfirmation && { width: 252 },
              ]}
            />

            {/* Manual enty confirmation */}
            {isManualEntry && !requiresConfirmation ? (
              <ManaButton
                isMainButton
                text="Update"
                disabled={!selectedTimeslot.hasUpdatedEndTime}
                onPress={() => saveTimeslotIntervals(selectedTimeslot)}
                style={[styles.button]}
              />
            ) : null}

            {requiresConfirmation ? (
              <ManaButton
                isMainButton
                text="Confirm"
                onPress={() => confirmTimeslotDuration(selectedTimeslot)}
                style={[styles.button]}
              />
            ) : null}
          </View>

          {isManualEntry ? null : (
            <TimePickerSwipeInfo
              controller={controller}
              timeslot={timeslot}
              allTimeslots={allTimeslots}
              use24hFormat={use24hFormat}
              onTimeChanged={onTimeChanged}
            />
          )}
        </Dialog.Container>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    flexDirection: "row",
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: "#F5F5F5",
    paddingHorizontal: 20,
  },
  button: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: "#2D3142",
    height: 34,
    width: "auto",
    paddingHorizontal: 20,
  },
});
